import { Stack } from '@mui/material';
import { useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { DeleteButton } from '@/components/form/baseInputs/Button/DeleteButton';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { makeActivateEntityUpdateInput } from '@/modules/entities/components/ConvertToActiveMenuItem/makeActivateEntityUpdateInput';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import {
  mapFormDataToUpdateInput,
  useSyncLiabilityDataToForm,
} from '@/modules/liabilities/LiabilityModal/EditLiabilityModal.utils';
import {
  useDeleteLiabilityMutation,
  useUpdateLiabilityWithEntityUpdatesMutation,
} from '@/modules/liabilities/LiabilityModal/graphql/LiabilityModal.generated';
import { useIssuerAndRecipientOptionsGrouped } from '@/modules/liabilities/LiabilityModal/hooks/useRecipientOptions';
import { LiabilityForm } from '@/modules/liabilities/LiabilityModal/LiabilityForm';
import { getLiabilityIntegrationPrefilledFields } from '@/modules/liabilities/LiabilityModal/LiabilityForm.integration';
import {
  defaultValues,
  LiabilityFormShape,
  NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL,
} from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import { EntityStage } from '@/types/schema';

export interface EditLiabilitySideDrawerProps {
  /**
   * Whether the drawer is open
   */
  isOpen: boolean;
  /**
   * Function to call when closing the drawer
   */
  onClose: () => void;
  /**
   * Optional close handler that also refreshes the owning view when the
   * liability no longer exists.
   */
  onNotFound?: () => void;
  /**
   * The liability ID being edited
   */
  liabilityId: string;
  /**
   * Optional entity ID if the liability is associated with a specific entity
   */
  entityId?: string;
  /**
   * Render read-only: hide the delete and save actions and disable the form
   * inputs. Used when the viewer lacks edit permission (the holdings menu
   * surfaces this drawer as "View …" rather than "Edit …").
   */
  isViewOnly?: boolean;
}

/**
 * Inner component that uses the form context
 */
function EditLiabilitySideDrawerInner({
  isOpen,
  onClose,
  liabilityId,
  entityId,
  isViewOnly = false,
}: EditLiabilitySideDrawerProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const trackUserEvent = useTrackUserEvent();
  const { handleSubmit, reset, formState } =
    useFormContext<LiabilityFormShape>();
  const { defaultValues: initialFormValues } = formState;
  // The selectable issuer/recipient entities, from the same source the form's
  // dropdowns use — so activating a PreCreated issuer/recipient on save works
  // wherever this drawer is mounted (it no longer needs EstateHoldingsProvider).
  const { entities } = useIssuerAndRecipientOptionsGrouped({
    currentEntityId: entityId,
  });
  const initialIntegrationPrefilledFields = useMemo(
    () => getLiabilityIntegrationPrefilledFields(initialFormValues),
    [initialFormValues]
  );

  const [deleteMutation, { loading: deleting }] = useDeleteLiabilityMutation({
    variables: { liabilityId },
    // A delete returns no entity payload, so Apollo can't auto-update the
    // cache. Evict the liability and refetch active queries (like the update
    // mutation) so lists that don't run their own refetch on close — e.g.
    // estate direct holdings liabilities — drop the removed row immediately.
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    update: (cache) => {
      cache.evict({ id: liabilityId });
    },
    onError: (error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError(`Could not delete liability`, error);
    },
    onCompleted: () => {
      handleClose();
      showFeedback('Liability successfully deleted', { variant: 'success' });
    },
  });

  const [updateMutation, { loading }] =
    useUpdateLiabilityWithEntityUpdatesMutation({
      refetchQueries: 'active',
      awaitRefetchQueries: true,
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError);
        reportError(`Could not edit liability`, error);
      },
    });

  const onSubmit = handleSubmit(async (formData) => {
    try {
      // Collect entities that need to be activated
      const entitiesToActivate = [];

      if (
        formData.issuerId &&
        formData.issuerId !== NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL
      ) {
        const issuerEntity = entities.find(
          (entity) => entity.id === formData.issuerId
        );
        if (issuerEntity) {
          entitiesToActivate.push(issuerEntity);
        }
      }

      if (
        formData.recipientId &&
        formData.recipientId !== NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL
      ) {
        const recipientEntity = entities.find(
          (entity) => entity.id === formData.recipientId
        );
        if (recipientEntity) {
          entitiesToActivate.push(recipientEntity);
        }
      }

      // Build entity update inputs for PreCreated entities
      const preCreatedEntities = entitiesToActivate.filter(
        (entity) => entity.stage === EntityStage.PreCreated
      );

      const entityUpdateInputs = preCreatedEntities.map((entity) => {
        const entityType = getEntityTypeFromEntityKind(entity.kind);
        return makeActivateEntityUpdateInput({
          entityId: entity.id,
          subtypeId: entity.subtype.id,
          entityType,
        });
      });

      const liabilityInput = mapFormDataToUpdateInput(
        initialFormValues as Readonly<LiabilityFormShape> | undefined,
        formData,
        liabilityId
      );

      // Update liability and entities in a single mutation
      await updateMutation({
        variables: {
          input: liabilityInput,
          updateEntities: entityUpdateInputs,
        },
        onCompleted: () => {
          trackUserEvent('liability updated', {
            includesPaymentData: Boolean(formData.paymentKind),
          });
          showFeedback('Successfully updated liability', {
            variant: 'success',
          });
          handleClose();
        },
      });
    } catch (error) {
      reportError('Unexpected error while updating liability', error as Error);
      throw error; // Re-throw to maintain existing error handling behavior
    }
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const body = (
    <Stack gap={2} mt={2} width="100%" flex={1}>
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <LiabilityForm
          entityId={entityId}
          initialIntegrationPrefilledFields={initialIntegrationPrefilledFields}
          hideIntegrationSelect
        />
      </form>
    </Stack>
  );

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={handleClose}
      heading={isViewOnly ? 'View liability' : 'Edit liability'}
      data-testid="edit-liability-side-drawer"
      body={
        isViewOnly ? (
          <HouseholdPermissionAwareDisabledProvider>
            {body}
          </HouseholdPermissionAwareDisabledProvider>
        ) : (
          body
        )
      }
      footer={
        isViewOnly ? (
          <Stack direction="row" justifyContent="flex-end" width="100%">
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Close
            </Button>
          </Stack>
        ) : (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            width="100%"
          >
            <DeleteButton
              size="sm"
              onConfirmDelete={() => deleteMutation()}
              loading={deleting}
              disabled={loading || deleting}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleClose}
                disabled={loading || deleting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                loading={loading}
                disabled={loading || deleting}
              >
                Save changes
              </Button>
            </Stack>
          </Stack>
        )
      }
    />
  );
}

/**
 * Side drawer for editing liabilities
 */
export function EditLiabilitySideDrawer(props: EditLiabilitySideDrawerProps) {
  const formMethods = useForm<LiabilityFormShape>({ defaultValues });
  useSyncLiabilityDataToForm(
    formMethods.reset,
    props.liabilityId,
    props.onNotFound ?? props.onClose
  );

  return (
    <FormProvider {...formMethods}>
      <EditLiabilitySideDrawerInner {...props} />
    </FormProvider>
  );
}
